// src/components/Engagement/PrayerWall.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { getInitials, getRandomColor } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getPrayers,
  getPrayer,
  createPrayer,
  updatePrayer,
  deletePrayer,
  markPrayerPrayed,
  respondToPrayer,
  escalatePrayer,
  sendPrayerDigest,
  getPrayerStats,
  exportPrayers,
  getPrayerSettings,
  updatePrayerSettings,
  updateAcknowledgmentTemplate,
  clearApiMessage,
  clearError,
  clearPrayerState,
  clearCurrentPrayer,
  clearExportData,
  clearDigestResult,
  selectPrayers,
  selectCurrentPrayer,
  selectPrayerStats,
  selectPrayerPagination,
  selectPrayerLoading,
  selectPrayerError,
  selectPrayerApiMessage,
  selectPrayerSuccess,
  selectPrayerExportData,
  selectPrayerStatsSummary,
  selectPrayerDigestResult,
  selectPrayerSettings,
  selectAcknowledgmentTemplate,
} from '../../store/slices/engagement/prayerSlice';
import { 
  getMembers, 
  selectMembers,
  selectMemberPagination,
  selectAvailableGroups 
} from '../../store/slices/main/memberSlice';
import { 
  getVisitors, 
  selectVisitors,
  selectVisitorPagination 
} from '../../store/slices/growth/visitorSlice';
import {
  Download, BarChart3, ClipboardList, TrendingUp, FileText, Siren, Clock, Mail,
  Pencil, Trash2, MessageSquare, AlertTriangle, Cross, Lock, Search, Settings,
  Save, Send, CheckCircle2, HandHeart, Loader2, Users, UserPlus
} from 'lucide-react';

// ==================== HELPER: Format Date & Time ====================
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return dateString;
  } catch (e) {
    return dateString;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    return dateString;
  } catch (e) {
    return dateString;
  }
};

const PrayerWall = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [urgentFilter, setUrgentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPrayerId, setEditingPrayerId] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dynamicGroupOptions, setDynamicGroupOptions] = useState([]);
  
  // Local settings state
  const [localSettings, setLocalSettings] = useState({
    auto_acknowledge: true,
    allow_anonymous: true,
    alert_pastor_for_urgent: true,
    send_weekly_digest: false,
    flag_crisis_keywords: true,
  });
  const [localTemplate, setLocalTemplate] = useState("");

  // Refs for edit form
  const editMemberRef = useRef(null);
  const editPrayerRef = useRef(null);
  const editGroupRef = useRef(null);
  const editUrgentRef = useRef(null);
  const editAnonymousRef = useRef(null);

  // Refs for create form
  const createMemberRef = useRef(null);
  const createPrayerRef = useRef(null);
  const createGroupRef = useRef(null);
  const createPhoneRef = useRef(null);
  const createEmailRef = useRef(null);
  const createUrgentRef = useRef(null);
  const createAnonymousRef = useRef(null);

  // Refs for modal forms
  const replyRef = useRef(null);
  const escalateRef = useRef(null);
  const digestRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const prayers = useSelector(selectPrayers) || [];
  const currentPrayer = useSelector(selectCurrentPrayer);
  const stats = useSelector(selectPrayerStatsSummary);
  const pagination = useSelector(selectPrayerPagination);
  const loading = useSelector(selectPrayerLoading);
  const apiMessage = useSelector(selectPrayerApiMessage);
  const error = useSelector(selectPrayerError);
  const successMessage = useSelector(selectPrayerSuccess);
  const exportData = useSelector(selectPrayerExportData);
  const digestResult = useSelector(selectPrayerDigestResult);
  const settings = useSelector(selectPrayerSettings);
  const template = useSelector(selectAcknowledgmentTemplate);
  
  // MEMBER AND VISITOR SELECTORS
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};
  const groups = useSelector(selectAvailableGroups) || [];

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingOptions(true);
      try {
        await Promise.all([
          dispatch(getPrayers({ 
            per_page: itemsPerPage, 
            page: currentPage,
            search: searchTerm,
            status: statusFilter,
            urgent: urgentFilter
          })),
          dispatch(getPrayerStats()),
          dispatch(getPrayerSettings()),
          dispatch(getMembers({ per_page: 50 })),
          dispatch(getVisitors({ per_page: 50 })),
        ]);
      } catch (err) {
        console.error('Error loading prayer data:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearPrayerState());
    };
  }, [dispatch]);

  // Build dynamic group options
  useEffect(() => {
    buildDynamicGroupOptions();
  }, [members, visitors, groups, memberPagination, visitorPagination]);

  // Sync settings from Redux to local state
  useEffect(() => {
    if (settings) {
      setLocalSettings({
        auto_acknowledge: settings.auto_acknowledge !== undefined ? settings.auto_acknowledge : true,
        allow_anonymous: settings.allow_anonymous !== undefined ? settings.allow_anonymous : true,
        alert_pastor_for_urgent: settings.alert_pastor_for_urgent !== undefined ? settings.alert_pastor_for_urgent : true,
        send_weekly_digest: settings.send_weekly_digest !== undefined ? settings.send_weekly_digest : false,
        flag_crisis_keywords: settings.flag_crisis_keywords !== undefined ? settings.flag_crisis_keywords : true,
      });
    }
  }, [settings]);

  // Sync template from Redux to local state
  useEffect(() => {
    if (template) {
      setLocalTemplate(template);
    }
  }, [template]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm,
        urgent: urgentFilter
      };
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getPrayers(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, urgentFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast) return;
    
    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== BUILD DYNAMIC GROUP OPTIONS ====================
  
  const buildDynamicGroupOptions = () => {
    const options = [];
    
    // 1. MEMBER GROUPS
    let groupsList = [];
    
    // Try from member pagination
    if (memberPagination?.groups && Array.isArray(memberPagination.groups)) {
      groupsList = memberPagination.groups;
    } 
    // Try from groups selector
    else if (groups && groups.length > 0) {
      groupsList = groups;
    }
    // Extract from members data
    else if (members && members.length > 0) {
      const uniqueGroups = new Set();
      members.forEach(m => {
        if (m.group) uniqueGroups.add(m.group);
      });
      groupsList = Array.from(uniqueGroups);
    }
    
    // Add member groups to options
    groupsList.forEach(group => {
      const groupMemberCount = members.filter(m => m.group === group).length;
      options.push({
        id: `group_${group}`,
        value: group,
        label: `👥 ${group} (${groupMemberCount} members)`,
        type: 'group'
      });
    });
    
    // 2. VISITOR SOURCES (as groups)
    const sources = new Set();
    visitors.forEach(v => {
      if (v.source) sources.add(v.source);
      if (v.referred_by) sources.add(v.referred_by);
      if (v.visitor_type) sources.add(v.visitor_type);
    });
    
    Array.from(sources).filter(Boolean).forEach(source => {
      const sourceCount = visitors.filter(v => 
        v.source === source || v.referred_by === source || v.visitor_type === source
      ).length;
      options.push({
        id: `source_${source}`,
        value: source,
        label: `📌 ${source} (${sourceCount} visitors)`,
        type: 'visitor_source'
      });
    });
    
    // 3. BRANCHES (as groups)
    const branches = new Set();
    members.forEach(m => {
      if (m.branch) branches.add(m.branch);
    });
    visitors.forEach(v => {
      if (v.branch) branches.add(v.branch);
    });
    
    Array.from(branches).filter(Boolean).forEach(branchName => {
      const memberCount = members.filter(m => m.branch === branchName).length;
      const visitorCount = visitors.filter(v => v.branch === branchName).length;
      const total = memberCount + visitorCount;
      options.push({
        id: `branch_${branchName}`,
        value: branchName,
        label: `🏛️ ${branchName} (${total} people)`,
        type: 'branch'
      });
    });
    
    // 4. Always include General
    options.unshift({
      id: 'general',
      value: 'General',
      label: '🌐 General',
      type: 'general'
    });
    
    setDynamicGroupOptions(options);
  };

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== EXPORT FUNCTIONS ====================
  const handleExport = async (type, prayerId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      const params = {};
      if (prayerId) params.prayer_id = prayerId;
      if (statusFilter) params.status = statusFilter;
      if (urgentFilter) params.urgent = urgentFilter;
      
      const result = await dispatch(exportPrayers({ type, params })).unwrap();
      console.log('Export result:', result);
      
      if (result.success && result.exportData) {
        let data = result.exportData;
        
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } 
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            data = Object.values(data);
          }
        }
        
        if (!Array.isArray(data) || data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }
        
        const filename = getExportFilename(`prayers-${type}`);
        
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Prayer Requests Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }
        
        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'all', prayer = null) => {
    let data = [];
    let title = 'Prayer Requests Report';
    let filename = 'prayer-requests';
    let dataCount = prayers.length;

    if (exportType === 'single' && prayer) {
      data = [{
        'Member': prayer.member_name || 'Anonymous',
        'Prayer Request': prayer.request,
        'Group': prayer.group || 'General',
        'Status': prayer.status,
        'Urgent': prayer.is_urgent ? 'Yes' : 'No',
        'Time Received': formatDateTime(prayer.created_at),
        'Escalated': prayer.is_escalated ? 'Yes' : 'No',
        'Response': prayer.response || '',
        'Responded At': prayer.responded_at ? formatDateTime(prayer.responded_at) : '',
      }];
      title = `Prayer Request: ${prayer.member_name || 'Anonymous'}`;
      filename = `prayer-${prayer.id}-${(prayer.member_name || 'anonymous').substring(0, 20)}`;
      dataCount = 1;
    } else if (exportType === 'pending') {
      const pendingPrayers = prayers.filter(p => p.status === 'Pending');
      data = pendingPrayers.map(p => ({
        'Member': p.member_name || 'Anonymous',
        'Prayer Request': p.request,
        'Group': p.group || 'General',
        'Urgent': p.is_urgent ? 'Yes' : 'No',
        'Time Received': formatDateTime(p.created_at),
        'Escalated': p.is_escalated ? 'Yes' : 'No',
      }));
      title = 'Pending Prayer Requests';
      filename = 'pending-prayers';
      dataCount = pendingPrayers.length;
    } else if (exportType === 'urgent') {
      const urgentPrayers = prayers.filter(p => p.is_urgent);
      data = urgentPrayers.map(p => ({
        'Member': p.member_name || 'Anonymous',
        'Prayer Request': p.request,
        'Group': p.group || 'General',
        'Status': p.status,
        'Time Received': formatDateTime(p.created_at),
        'Escalated': p.is_escalated ? 'Yes' : 'No',
      }));
      title = 'Urgent Prayer Requests';
      filename = 'urgent-prayers';
      dataCount = urgentPrayers.length;
    } else if (exportType === 'summary') {
      const total = stats?.total || 0;
      const pending = stats?.pending || 0;
      const prayed = stats?.prayed || 0;
      const urgent = stats?.urgent || 0;
      const escalated = stats?.escalated || 0;
      const anonymous = stats?.anonymous || 0;
      
      data = [
        { 'Metric': 'Total Prayer Requests', 'Value': total },
        { 'Metric': 'Pending', 'Value': pending },
        { 'Metric': 'Prayed', 'Value': prayed },
        { 'Metric': 'Responded', 'Value': stats?.responded || 0 },
        { 'Metric': 'Urgent', 'Value': urgent },
        { 'Metric': 'Escalated', 'Value': escalated },
        { 'Metric': 'Anonymous', 'Value': anonymous },
      ];
      title = 'Prayer Wall Summary';
      filename = 'prayer-summary';
      dataCount = data.length;
    } else {
      data = prayers.map(p => ({
        'Member': p.member_name || 'Anonymous',
        'Prayer Request': p.request,
        'Group': p.group || 'General',
        'Status': p.status,
        'Urgent': p.is_urgent ? 'Yes' : 'No',
        'Time Received': formatDateTime(p.created_at),
        'Escalated': p.is_escalated ? 'Yes' : 'No',
        'Response': p.response || '',
        'Responded At': p.responded_at ? formatDateTime(p.responded_at) : '',
      }));
      title = 'All Prayer Requests';
      filename = 'all-prayers';
      dataCount = data.length;
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {dataCount} prayer request{dataCount !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, prayer?.id, format)}
                  disabled={isSubmitting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{dataCount} prayer requests will be exported</span>
            <span style={{ fontSize: '11px' }}>{title}</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAll = () => {
    if (prayers.length === 0) {
      showToastMessage('No prayer requests to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportPrayer = (prayer) => {
    openExportModal('single', prayer);
  };

  const handleExportSummary = () => {
    if (prayers.length === 0) {
      showToastMessage('No prayer requests to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  const handleExportPending = () => {
    const pending = prayers.filter(p => p.status === 'Pending');
    if (pending.length === 0) {
      showToastMessage('No pending prayer requests to export', 'info');
      return;
    }
    openExportModal('pending');
  };

  const handleExportUrgent = () => {
    const urgent = prayers.filter(p => p.is_urgent);
    if (urgent.length === 0) {
      showToastMessage('No urgent prayer requests to export', 'info');
      return;
    }
    openExportModal('urgent');
  };

  // ==================== SETTINGS HANDLERS ====================

  const handleSettingToggle = async (settingKey) => {
    const newValue = !localSettings[settingKey];
    setLocalSettings(prev => ({
      ...prev,
      [settingKey]: newValue
    }));

    try {
      const result = await dispatch(updatePrayerSettings({
        [settingKey]: newValue
      })).unwrap();
      
      if (result.success) {
        showToastMessage(`✅ ${settingKey.replace(/_/g, ' ')} updated successfully!`, 'success');
        await dispatch(getPrayerSettings());
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to update settings', 'error');
      // Revert local state
      setLocalSettings(prev => ({
        ...prev,
        [settingKey]: !newValue
      }));
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(updateAcknowledgmentTemplate({
        template: localTemplate
      })).unwrap();
      
      if (result.success) {
        showToastMessage('💾 Acknowledgement template saved!', 'success');
        await dispatch(getPrayerSettings());
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to save template', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(updatePrayerSettings(localSettings)).unwrap();
      
      if (result.success) {
        showToastMessage('💾 Prayer wall settings saved!', 'success');
        await dispatch(getPrayerSettings());
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to save settings', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== PRAYER CRUD ====================

  const openCreatePrayer = () => {
    setTimeout(() => {
      if (createMemberRef.current) createMemberRef.current.value = '';
      if (createPrayerRef.current) createPrayerRef.current.value = '';
      if (createGroupRef.current) createGroupRef.current.value = 'General';
      if (createPhoneRef.current) createPhoneRef.current.value = '';
      if (createEmailRef.current) createEmailRef.current.value = '';
      if (createUrgentRef.current) createUrgentRef.current.checked = false;
      if (createAnonymousRef.current) createAnonymousRef.current.checked = false;
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><HandHeart size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create Prayer Request</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Member Name</label>
            <input 
              className="form-input" 
              placeholder="John Doe"
              ref={createMemberRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prayer Request <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={createPrayerRef}
              placeholder="Please pray for..."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Group</label>
              <select 
                className="form-select"
                ref={createGroupRef}
                defaultValue="General"
              >
                {isLoadingOptions ? (
                  <option value="" disabled>Loading groups...</option>
                ) : dynamicGroupOptions.length > 0 ? (
                  dynamicGroupOptions.map(option => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No groups available</option>
                )}
              </select>
              <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
                <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
                <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {dynamicGroupOptions.length} group options</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input 
                className="form-input" 
                type="tel"
                placeholder="+1234567890"
                ref={createPhoneRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input 
                className="form-input" 
                type="email"
                placeholder="john@example.com"
                ref={createEmailRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" ref={createUrgentRef} />
                Mark as Urgent
              </label>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" ref={createAnonymousRef} />
                Post Anonymously
              </label>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const prayer = createPrayerRef.current?.value?.trim() || '';
              
              if (!prayer) {
                showToastMessage('Please enter a prayer request', 'error');
                return;
              }
              
              const data = {
                member_name: createMemberRef.current?.value?.trim() || 'Anonymous',
                request: prayer,
                group: createGroupRef.current?.value || 'General',
                phone: createPhoneRef.current?.value || null,
                email: createEmailRef.current?.value || null,
                is_urgent: createUrgentRef.current?.checked || false,
                is_anonymous: createAnonymousRef.current?.checked || false,
              };
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createPrayer(data)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPrayerStats());
                  showToastMessage(`🙏 Prayer request created successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to create prayer request', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Prayer Request'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditPrayer = (prayer) => {
    setEditingPrayerId(prayer.id);
    
    setTimeout(() => {
      if (editMemberRef.current) editMemberRef.current.value = prayer.member_name || '';
      if (editPrayerRef.current) editPrayerRef.current.value = prayer.request || '';
      if (editGroupRef.current) editGroupRef.current.value = prayer.group || 'General';
      if (editUrgentRef.current) editUrgentRef.current.checked = prayer.is_urgent || false;
      if (editAnonymousRef.current) editAnonymousRef.current.checked = prayer.is_anonymous || false;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Prayer Request</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Member Name</label>
            <input 
              className="form-input" 
              ref={editMemberRef}
              defaultValue={prayer.member_name || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prayer Request <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={editPrayerRef}
              defaultValue={prayer.request || ''}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Group</label>
              <select 
                className="form-select"
                ref={editGroupRef}
                defaultValue={prayer.group || 'General'}
              >
                {isLoadingOptions ? (
                  <option value="" disabled>Loading groups...</option>
                ) : dynamicGroupOptions.length > 0 ? (
                  dynamicGroupOptions.map(option => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No groups available</option>
                )}
              </select>
              <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
                <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" ref={editUrgentRef} defaultChecked={prayer.is_urgent || false} />
                Mark as Urgent
              </label>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" ref={editAnonymousRef} defaultChecked={prayer.is_anonymous || false} />
                Post Anonymously
              </label>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const request = editPrayerRef.current?.value?.trim() || '';
              
              if (!request) {
                showToastMessage('Please enter a prayer request', 'error');
                return;
              }
              
              const data = {
                member_name: editMemberRef.current?.value?.trim() || 'Anonymous',
                request: request,
                group: editGroupRef.current?.value || 'General',
                is_urgent: editUrgentRef.current?.checked || false,
                is_anonymous: editAnonymousRef.current?.checked || false,
              };
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updatePrayer({
                  id: prayer.id,
                  data
                })).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPrayerStats());
                  showToastMessage(`✅ Prayer request updated successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update prayer request', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
              : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeletePrayer = (prayer) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Prayer Request</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this prayer request from <strong>{prayer.member_name || 'Anonymous'}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deletePrayer(prayer.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPrayerStats());
                  showToastMessage(`🗑️ Prayer request from ${prayer.member_name || 'Anonymous'} deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete prayer request', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Prayer
          </button>
        </div>
      </div>
    );
  };

  const handleMarkPrayed = async (prayer) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(markPrayerPrayed(prayer.id)).unwrap();
      if (result.success) {
        await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getPrayerStats());
        showToastMessage(`🙏 "${prayer.request.substring(0, 30)}..." marked as prayed for!`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to mark prayer as prayed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = (prayer) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><MessageSquare size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Reply to Prayer Request</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>From: <strong>{prayer.member_name || 'Anonymous'}</strong></div>
            <div style={{ fontSize: '13px', color: 'var(--text)', fontStyle: 'italic' }}>"{prayer.request}"</div>
          </div>
          <div className="form-group">
            <label className="form-label">Reply Message</label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              ref={replyRef}
              placeholder="Type your reply here…" 
              defaultValue={`Dear ${prayer.member_name || 'Beloved'}, thank you for sharing your prayer request. Our team is praying for you. God bless you! 🙏`} 
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const response = replyRef.current?.value?.trim() || '';
              if (!response) {
                showToastMessage('Please enter a reply message', 'error');
                return;
              }
              setIsSubmitting(true);
              try {
                const result = await dispatch(respondToPrayer({
                  id: prayer.id,
                  data: { response }
                })).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPrayerStats());
                  showToastMessage(`💬 Reply sent to ${prayer.member_name || 'Anonymous'}!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send reply', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Send Reply
          </button>
        </div>
      </div>
    );
  };

  const handleEscalate = (prayer) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Siren size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Escalate Prayer Request</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--red-bg)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--red)', fontWeight: '600' }}><AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Escalate to Pastoral Team</div>
            <div style={{ fontSize: '12px', color: 'var(--red)', marginTop: '4px' }}>
              This will send an urgent notification to the pastoral team.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes for Pastoral Team</label>
            <textarea 
              className="form-textarea" 
              rows="3" 
              ref={escalateRef}
              placeholder="Any additional context…" 
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const notes = escalateRef.current?.value?.trim() || '';
              setIsSubmitting(true);
              try {
                const result = await dispatch(escalatePrayer({
                  id: prayer.id,
                  notes
                })).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPrayerStats());
                  showToastMessage(`🚨 Prayer request escalated to pastoral team!`, 'info');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to escalate prayer', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Escalate
          </button>
        </div>
      </div>
    );
  };

  const handleSendDigest = async () => {
    const pendingPrayers = prayers.filter(p => p.status === 'Pending');
    if (pendingPrayers.length === 0) {
      showToastMessage('No pending prayer requests to send', 'info');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Prayer Digest</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--gold-pale)', border: '1px solid #f0dfa0', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', color: 'var(--navy)', marginBottom: '8px' }}><Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Prayer Digest</div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send <strong>{pendingPrayers.length}</strong> pending prayer requests to all members.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Digest Message</label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              ref={digestRef}
              defaultValue={`Dear Members, please join us in prayer for these requests:\n\n${pendingPrayers.map(p => `- ${p.member_name || 'Anonymous'}: ${p.request}`).join('\n')}\n\nThank you for praying! 🙏 — Grace Chapel`} 
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(sendPrayerDigest()).unwrap();
                if (result.success) {
                  closeModal();
                  showToastMessage(`📨 Prayer digest sent to all members!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send prayer digest', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Send Digest
          </button>
        </div>
      </div>
    );
  };

  const handleMarkAllPrayed = () => {
    const pendingPrayers = prayers.filter(p => p.status === 'Pending');
    if (pendingPrayers.length === 0) {
      showToastMessage('No pending prayer requests to mark', 'info');
      return;
    }
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><HandHeart size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Mark All as Prayed</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to mark all <strong>{pendingPrayers.length}</strong> pending prayer requests as prayed?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will update all pending requests to "Prayed" status.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                let successCount = 0;
                for (const prayer of pendingPrayers) {
                  try {
                    await dispatch(markPrayerPrayed(prayer.id)).unwrap();
                    successCount++;
                  } catch (e) {
                    console.error('Failed to mark prayer:', prayer.id, e);
                  }
                }
                await dispatch(getPrayers({ per_page: itemsPerPage, page: currentPage }));
                await dispatch(getPrayerStats());
                showToastMessage(`🙏 ${successCount} prayer requests marked as prayed!`, 'success');
                closeModal();
              } catch (err) {
                showToastMessage(err.message || 'Failed to mark prayers as prayed', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Mark All as Prayed
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    total: 0,
    pending: 0,
    prayed: 0,
    responded: 0,
    urgent: 0,
    escalated: 0,
    anonymous: 0,
    weeklyData: [],
    groupStats: []
  };

  const currentItems = prayers;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <HandHeart size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Members text their prayer requests to your shortcode. Aggregate and respond with care.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export prayer wall summary"
            disabled={isSubmitting}
          >
            <ClipboardList size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportPending}
            title="Export pending prayer requests"
            disabled={isSubmitting}
          >
            <Clock size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Pending
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportUrgent}
            title="Export urgent prayer requests"
            disabled={isSubmitting}
          >
            <Siren size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Urgent
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all prayer requests"
            disabled={isSubmitting}
          >
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export All
          </button>
          <button
            className="btn btn-gold btn-sm"
            onClick={handleSendDigest}
            title="Send prayer digest to all members"
            disabled={isSubmitting}
          >
            <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Prayer Digest
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleMarkAllPrayed}
            title="Mark all pending requests as prayed"
            disabled={isSubmitting}
          >
            <HandHeart size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Mark All Prayed
          </button>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={openCreatePrayer} 
            title="Create a new prayer request"
            disabled={isSubmitting}
          >
            + Add Prayer
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Requests</div>
          <div className="stat-value">{statsData.total}</div>
          <div className="stat-change up">↑ this week</div>
          <div className="stat-icon"><HandHeart size={20} /></div>
        </div>
        <div className="stat-card red" style={{ '--stat-color': 'var(--red)', cursor: 'default' }}>
          <div className="stat-label">Urgent / Flagged</div>
          <div className="stat-value">{statsData.urgent}</div>
          <div className="stat-change down">needs attention</div>
          <div className="stat-icon"><Siren size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Prayed For</div>
          <div className="stat-value">{statsData.prayed}</div>
          <div className="stat-change up">↑ responded</div>
          <div className="stat-icon"><Cross size={20} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Anonymous</div>
          <div className="stat-value">{statsData.anonymous}</div>
          <div className="stat-change up">private requests</div>
          <div className="stat-icon"><Lock size={20} /></div>
        </div>
      </div>

      <div className="grid-65-35">
        <div>
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
              <div className="card-title"><HandHeart size={16} style={{ verticalAlign: '-3px' }} /> Prayer Requests</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div className="search-box" style={{ maxWidth: '180px' }}>
                  <span className="search-icon"><Search size={16} /></span>
                  <input 
                    type="text" 
                    placeholder="Search…" 
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    title="Search by member, prayer, or group"
                  />
                </div>
                <select 
                  className="form-select" 
                  style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  title="Filter by status"
                  disabled={loading}
                >
                  <option value="">All Requests</option>
                  <option value="Pending">Pending</option>
                  <option value="Prayed">Prayed</option>
                  <option value="Responded">Responded</option>
                </select>
                <select 
                  className="form-select" 
                  style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
                  value={urgentFilter}
                  onChange={(e) => {
                    setUrgentFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  title="Filter by urgency"
                  disabled={loading}
                >
                  <option value="">All Urgency</option>
                  <option value="urgent">Urgent</option>
                  <option value="normal">Normal</option>
                </select>
              </div>
            </div>

            {loading && currentItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
                <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading prayer requests...</div>
              </div>
            ) : currentItems.length > 0 ? (
              currentItems.map(p => {
                const color = getRandomColor(p.id);
                const statusCls = p.status === 'Prayed' ? 'badge-green' : p.status === 'Responded' ? 'badge-blue' : 'badge-amber';
                return (
                  <div key={p.id} style={{ 
                    border: `1px solid ${p.is_urgent ? '#fca5a5' : 'var(--border)'}`, 
                    borderRadius: '10px', 
                    padding: '14px', 
                    marginBottom: '10px', 
                    background: p.is_urgent ? '#fff8f8' : 'var(--white)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: 'white', flexShrink: 0 }}>
                        {(p.member_name || 'A')[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                          {p.member_name || 'Anonymous'} {p.is_urgent && <span style={{ fontSize: '10px', color: 'var(--red)', fontWeight: '700' }}><Siren size={11} style={{ verticalAlign: '-2px' }} /> URGENT</span>}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{p.group || 'General'} · {formatDateTime(p.created_at)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span className={`badge ${statusCls}`}>{p.status}</span>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon" 
                          onClick={() => handleExportPrayer(p)}
                          title="Export this prayer request"
                          disabled={isSubmitting}
                        >
                          <Download size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleEditPrayer(p)}
                          title="Edit prayer request"
                          disabled={isSubmitting}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeletePrayer(p)}
                          title="Delete prayer request"
                          style={{ color: 'var(--red)' }}
                          disabled={isSubmitting}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
                      "{p.request}"
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {p.status === 'Pending' && (
                        <button 
                          className="btn btn-green btn-sm" 
                          style={{ background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid #a7d7be', fontSize: '12px', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }} 
                          onClick={() => handleMarkPrayed(p)}
                          disabled={isSubmitting}
                        >
                          <HandHeart size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Mark Prayed
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleReply(p)}
                        disabled={isSubmitting}
                      >
                        <MessageSquare size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Reply
                      </button>
                      {p.is_urgent && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleEscalate(p)}
                          disabled={isSubmitting}
                        >
                          <Siren size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Escalate
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <div className="empty-icon"><HandHeart size={32} strokeWidth={1.5} /></div>
                <div className="empty-title">No prayer requests found</div>
                <div className="empty-text">Try adjusting your search or filters</div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                paddingTop: '14px',
                borderTop: '1px solid var(--border)',
                marginTop: '4px'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                  Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> requests
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                    <select 
                      className="form-select" 
                      style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      title="Items per page"
                      disabled={loading}
                    >
                      <option value={3}>3</option>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToPreviousPage}
                      disabled={currentPageNum === 1 || loading}
                      style={{ 
                        opacity: currentPageNum === 1 ? 0.5 : 1, 
                        cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                      }}
                      title="Previous page"
                    >
                      ← Prev
                    </button>
                    
                    {getPageNumbers().map(page => (
                      <button 
                        key={page}
                        className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => goToPage(page)}
                        title={`Go to page ${page}`}
                        disabled={loading}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToNextPage}
                      disabled={currentPageNum === totalPages || loading}
                      style={{ 
                        opacity: currentPageNum === totalPages ? 0.5 : 1, 
                        cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                      }}
                      title="Next page"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-title" style={{ marginBottom: '14px' }}><Settings size={16} style={{ verticalAlign: '-3px' }} /> Prayer Wall Settings</div>
            <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Members text prayer requests to:</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>PRAY → 40404</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>or text PRAYER · REQUEST</div>
            </div>
            {[
              { key: 'auto_acknowledge', label: 'Auto-acknowledge requests' },
              { key: 'allow_anonymous', label: 'Allow anonymous requests' },
              { key: 'alert_pastor_for_urgent', label: 'Alert pastor for urgent requests' },
              { key: 'send_weekly_digest', label: 'Send weekly prayer digest' },
              { key: 'flag_crisis_keywords', label: 'Flag mentions of crisis keywords' }
            ].map(s => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '13px' }}>{s.label}</span>
                <label className="toggle" title={localSettings[s.key] ? 'Click to disable' : 'Click to enable'}>
                  <input 
                    type="checkbox" 
                    checked={localSettings[s.key] || false}
                    onChange={() => handleSettingToggle(s.key)}
                    disabled={isSubmitting}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
            <button 
              className="btn btn-gold btn-sm" 
              style={{ marginTop: '14px', width: '100%' }} 
              onClick={handleSaveSettings}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
                : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Settings</>}
            </button>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: '12px' }}><MessageSquare size={16} style={{ verticalAlign: '-3px' }} /> Auto-Acknowledge SMS</div>
            <textarea 
              className="form-textarea" 
              rows="3" 
              value={localTemplate} 
              onChange={(e) => setLocalTemplate(e.target.value)}
              title="Template sent when a prayer request is received"
              disabled={loading}
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
            <button 
              className="btn btn-ghost btn-sm" 
              style={{ marginTop: '8px' }} 
              onClick={handleSaveTemplate}
              disabled={isSubmitting || loading}
            >
              {isSubmitting
                ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
                : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Template</>}
            </button>
          </div>

          {/* Quick Stats */}
          <div className="card" style={{ marginTop: '16px' }}>
            <div className="card-title" style={{ marginBottom: '12px' }}><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Quick Stats</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                  {statsData.pending}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Pending</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--green)' }}>
                  {statsData.prayed}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Prayed</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--red)' }}>
                  {statsData.urgent}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Urgent</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--blue)' }}>
                  {statsData.responded}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Responded</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .badge-blue {
          background: var(--blue);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-green {
          background: var(--green);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-red {
          background: var(--red);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-amber {
          background: var(--amber);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-purple {
          background: #7c3aed;
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .progress-bar {
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--border);
          transition: .3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: .3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
      `}</style>
    </>
  );
};

export default PrayerWall;